import React from 'react';
import styled from 'styled-components';
import { AuthContext } from 'contexts';

import Badge from '../Badge';
import Empty from '../Empty';
import { List, ListItem } from '../List';
import Spin from '../Spin';
import theme from '../theme';

type ContentListProps = {
  items?: {
    AvatarComponent: React.ReactNode;
    DateComponent?: React.ReactNode;
    MainComponent: React.ReactNode;
    OpComponent?: React.ReactNode;
    date: Date | string;
    onItemClick: () => void;
    showBadge: boolean;
    selected?: boolean;
    appointmentId?: string;
    notificationId?: string;
  }[];
  isLoading?: boolean;
  emptyDescription?: string;
  loadMore?: React.ReactNode;
};

const ContentList: React.FC<ContentListProps> = ({
  items,
  isLoading,
  emptyDescription,
  loadMore = null,
}) => {
  const { auth } = React.useContext(AuthContext);
  if (items.length === 0 && !isLoading)
    return <Empty description={emptyDescription} />;

  if (isLoading) {
    return (
      <StyledList>
        <div className="spin-wrapper">
          <Spin size="large" />
        </div>
      </StyledList>
    );
  }

  return (
    <StyledList loadMore={loadMore}>
      {items.map(
        (
          {
            onItemClick,
            AvatarComponent,
            DateComponent,
            MainComponent,
            OpComponent,
            showBadge,
            selected,
            appointmentId,
            notificationId,
          },
          i,
        ) => (
          <StyledListItem
            key={i}
            $selected={selected}
            data-cy={`notification-${notificationId}-appointment-${appointmentId}`}
          >
            <div className="item-wrapper" onClick={() => onItemClick()}>
              <div className="avatar-wrapper">{AvatarComponent}</div>
              {MainComponent}
              <div>
                <div className="op-wrapper">{OpComponent}</div>
                <div className="date-wrapper">{DateComponent}</div>
                <div className="badge-wrapper">
                  {/* badge should not show if mls staff user is assuming another user */}
                  {showBadge && !auth?.assumerIdMlsStaff && <Badge dot />}
                </div>
              </div>
            </div>
          </StyledListItem>
        ),
      )}
    </StyledList>
  );
};

const StyledList = styled(List)`
  .spin-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100px;
    width: 100%;
  }
`;

const StyledListItem = styled(ListItem)<{ $selected: boolean }>`
  ${({ $selected }) => $selected && `background-color: ${theme.gray3}`};
  padding: 12px;
  position: relative;
  && {
    > .item-wrapper {
      display: grid;
      grid-template-columns: 62px 1fr auto;
      gap: 10px;
      width: 100%;
      cursor: pointer;
    }
    .avatar-wrapper {
      position: relative;
    }
    .badge-wrapper {
      text-align: right;
      transform: translateX(-4px);
    }
    .date-wrapper {
      > * {
        width: 100%;
        display: inline-block;
        text-align: right;
      }
    }
    .op-wrapper {
      text-align: right;
      opacity: 0;
    }
  }
  &&:hover {
    .op-wrapper {
      opacity: 1;
    }
  }
`;

export default ContentList;
